import { shallowRef, triggerRef } from 'vue'

const READ_KEY = 'adx-sms-testing-read'
const SENT_KEY = 'adx-sms-testing-sent'
const MAX_IDS = 500

function load(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return new Set(Array.isArray(parsed) ? parsed : [])
  } catch {
    return new Set()
  }
}

const readIds = shallowRef<Set<string>>(load(READ_KEY))
const sentIds = shallowRef<Set<string>>(load(SENT_KEY))

function persist(key: string, set: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(set).slice(-MAX_IDS)))
  } catch {
    // Storage disabled — degrade silently.
  }
}

/**
 * SMS read/unread + sent tracking. Read state mirrors the email composable
 * (rows are unread until opened). Sent is a separate set populated when the
 * user composes via the UI — gives the Sent view tab something to filter on
 * since SMS doesn't have a "from me" address heuristic the way email does.
 */
export function useSmsReadState() {
  function isRead(id: string): boolean {
    return readIds.value.has(id)
  }
  function markRead(id: string): void {
    if (readIds.value.has(id)) return
    readIds.value.add(id)
    triggerRef(readIds)
    persist(READ_KEY, readIds.value)
  }
  function markUnread(id: string): void {
    if (!readIds.value.has(id)) return
    readIds.value.delete(id)
    triggerRef(readIds)
    persist(READ_KEY, readIds.value)
  }
  function markSent(id: string): void {
    if (sentIds.value.has(id)) return
    sentIds.value.add(id)
    triggerRef(sentIds)
    persist(SENT_KEY, sentIds.value)
  }
  function isSent(id: string): boolean {
    return sentIds.value.has(id)
  }
  return { isRead, markRead, markUnread, markSent, isSent, readIds, sentIds }
}
